/** `<input type="number">` accepts exponent form — it is a valid floating-point number per the spec. */
const EXPONENT_NOTATION = /^-?\d*[.,]?\d*e[+-]?\d+$/i;

/**
 * Constrains a raw `<input>` value to `maxDecimals` decimal places.
 *
 * @param rawValue    The field's current value, exactly as the user left it.
 * @param maxDecimals Decimal places allowed. `0` means integers only.
 * @returns A TRI-STATE, and callers must handle all three:
 *  - `null`   — already acceptable, change nothing (lets a caller avoid rewriting what was typed).
 *  - `''`     — WIPE the field: exponent notation, which has no reading at a fixed decimal count.
 *  - a string — the replacement value.
 *
 * TRUNCATES, so a rate is never inflated to one the trader did not type: at two decimals `0.125`
 * is `0.12`, not `0.13`. Two decimals of percent is exactly one basis point, and a trader may not
 * book a fraction of one.
 *
 * By string slice, deliberately, NOT by `Math.trunc(value * 10 ** maxDecimals)`: `8.29 * 100` is
 * `828.9999999999999`, so arithmetic truncation would shave a basis point off a value that was
 * already valid. Slicing the digits has no floating point in it.
 *
 * A value still being typed is left alone (`"1."`, `"-"`) EXCEPT at `maxDecimals: 0`, where the
 * separator itself is not allowed and `"1."` becomes `"1"`.
 *
 * A comma separator is accepted but always normalised to `"."`, so a comma-locale entry is rewritten.
 * Reachable only programmatically in practice — `<input type="number">` rejects a comma on the way in.
 */
export function sanitizeDecimalPlaces(rawValue: string, maxDecimals: number): string | null {
  if (EXPONENT_NOTATION.test(rawValue ?? '')) {
    return '';
  }

  const match = rawValue?.match(/^(-?\d*)[.,](\d*)$/);
  if (!match) {
    return null;
  }

  const [, integerPart, decimals] = match;

  if (maxDecimals === 0) {
    return integerPart;
  }
  if (decimals.length <= maxDecimals) {
    return null;
  }

  return `${integerPart}.${decimals.slice(0, maxDecimals)}`;
}
